<template>
  <!-- EcountEtl 제품 목록 (SPEC-ECOUNT-FE-POLISH-001 Phase B-3 — 딜러 패턴 답습) -->
  <div class="page-container-v2">
    <div class="page-header">
      <span class="page-title">{{ $t('ecountEtl_products_title') }}</span>
      <div style="display: flex; align-items: center; gap: 8px; margin-left: auto;">
        <timezone-toggle :current-tz="currentTz" @toggle="toggleTz" />
        <a-button size="small" @click="showShortcutHelp = true">?</a-button>
      </div>
    </div>

    <!-- KR / CN / raw 탭 — 각 탭 옆에 컬렉션명 ? 툴팁 -->
    <a-tabs v-model:activeKey="activeTab" @change="onTabChange">
      <a-tab-pane key="all">
        <template #tab>
          <span class="tab-with-tip">
            {{ $t('ecountEtl_products_tab_all') }}
            <a-tooltip :title="$t('ecountEtl_products_tab_all_collection')">
              <QuestionCircleOutlined class="tab-tip-icon" />
            </a-tooltip>
          </span>
        </template>
      </a-tab-pane>
      <a-tab-pane key="kr">
        <template #tab>
          <span class="tab-with-tip">
            {{ $t('ecountEtl_products_tab_kr') }}
            <a-tooltip :title="$t('ecountEtl_products_tab_kr_collection')">
              <QuestionCircleOutlined class="tab-tip-icon" />
            </a-tooltip>
          </span>
        </template>
      </a-tab-pane>
      <a-tab-pane key="cn">
        <template #tab>
          <span class="tab-with-tip">
            {{ $t('ecountEtl_products_tab_cn') }}
            <a-tooltip :title="$t('ecountEtl_products_tab_cn_collection')">
              <QuestionCircleOutlined class="tab-tip-icon" />
            </a-tooltip>
          </span>
        </template>
      </a-tab-pane>
    </a-tabs>

    <!-- 검색 -->
    <a-card style="margin-bottom: 16px;">
      <a-form layout="inline" class="dealer-filter-form" :model="filters" @finish="onSearch">
        <a-form-item>
          <a-input
            ref="searchInput"
            v-model:value="filters.keyword"
            :placeholder="$t('ecountEtl_products_search_placeholder')"
            allow-clear
            style="min-width: 240px;"
          />
        </a-form-item>
        <a-form-item class="filter-actions">
          <a-space>
            <a-button type="primary" html-type="submit" :loading="loading">
              {{ $t('ecountEtl_common_search') }}
            </a-button>
            <a-button @click="onReset">{{ $t('ecountEtl_common_reset') }}</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 오류 -->
    <a-alert
      v-if="error"
      type="error"
      :message="error"
      style="margin-bottom: 16px;"
      show-icon
      closable
      @close="error = null"
    />

    <!-- 툴바: 총건수 + pageSize -->
    <div class="toolbar-row">
      <span class="total-count">
        {{ $t('ecountEtl_common_total') }}: {{ formatNumber(pagination.total) }}
      </span>
      <div class="toolbar-controls">
        <a-select v-model:value="pageSize" size="small" style="width: 100px;" @change="onToolbarPageSizeChange">
          <a-select-option :value="10">10 / {{ $t('ecountEtl_common_page') }}</a-select-option>
          <a-select-option :value="20">20 / {{ $t('ecountEtl_common_page') }}</a-select-option>
          <a-select-option :value="50">50 / {{ $t('ecountEtl_common_page') }}</a-select-option>
          <a-select-option :value="100">100 / {{ $t('ecountEtl_common_page') }}</a-select-option>
        </a-select>
      </div>
    </div>

    <!-- 데스크탑: a-table (가로 스크롤) — 탭별 columns 다름 -->
    <a-table
      v-if="!isMobileView"
      :data-source="products"
      :columns="columns"
      :loading="loading"
      :pagination="false"
      row-key="_id"
      size="small"
      :scroll="{ x: tableScrollX }"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'status'">
          <a-tag v-if="record.status" :color="record.status === 'active' ? 'green' : 'default'">{{ record.status }}</a-tag>
          <span v-else>-</span>
        </template>
        <template v-else-if="column.key === 'deleteYn'">
          <a-tag :color="record.deleteYn === 'Y' ? 'red' : 'default'">{{ record.deleteYn || '-' }}</a-tag>
        </template>
        <template v-else-if="column.key === 'gtinChecksumValid'">
          <a-tag :color="record.gtinChecksumValid ? 'green' : 'red'">{{ record.gtinChecksumValid ? '✓' : '✗' }}</a-tag>
        </template>
        <template v-else-if="column.key === 'isMapped'">
          <a-tag :color="record.isMapped ? 'green' : 'default'">{{ record.isMapped ? '✓' : '-' }}</a-tag>
        </template>
        <template v-else-if="['createdAt','updatedAt','importedAt'].includes(column.key)">
          {{ formatDateTime(record[column.dataIndex]) }}
        </template>
        <template v-else-if="column.key === 'names'">
          <EllipsisCell :text="record.names ? (record.names.ko || record.names.zh || record.names.en || '') : ''" />
        </template>
        <template v-else-if="['diameter','originalRowNo'].includes(column.key)">
          {{ formatNumber(record[column.dataIndex]) }}
        </template>
      </template>
    </a-table>

    <!-- 모바일: 카드뷰 — 탭별 분기 -->
    <div v-else class="mobile-card-list">
      <div v-if="products.length === 0 && !loading" class="empty-state">
        <a-empty :description="$t('ecountEtl_common_empty')" />
      </div>

      <!-- all 탭: ecount_product_master -->
      <template v-if="activeTab === 'all'">
        <div v-for="record in products" :key="record._id" class="mobile-card">
          <div class="card-header">
            <span class="card-gtin mono">{{ record.gtinCode || '-' }}</span>
            <a-tag v-if="record.status" :color="record.status === 'active' ? 'green' : 'default'">{{ record.status }}</a-tag>
          </div>
          <div class="card-field card-product-name">
            <span class="card-label">{{ $t('ecountEtl_products_col_productName') }}</span>
            <span class="card-value">{{ record.productName || '-' }}</span>
          </div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_ecountProductCode') }}</span><span class="card-value mono">{{ record.ecountProductCode || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_gtin14') }}</span><span class="card-value mono">{{ record.gtin14 || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_classCode') }}</span><span class="card-value">{{ [record.classCode, record.classCode2, record.classCode3].filter(Boolean).join(' / ') || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_prodType') }}</span><span class="card-value">{{ record.prodType || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_unit') }}</span><span class="card-value">{{ record.unit || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_regulatoryCode') }}</span><span class="card-value">{{ record.regulatoryCode || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_gtinChecksumValid') }}</span><span class="card-value"><a-tag :color="record.gtinChecksumValid ? 'green' : 'red'">{{ record.gtinChecksumValid ? '✓' : '✗' }}</a-tag></span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_deleteYn') }}</span><span class="card-value"><a-tag :color="record.deleteYn === 'Y' ? 'red' : 'default'">{{ record.deleteYn || '-' }}</a-tag></span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_createdAt') }}</span><span class="card-value">{{ formatDateTime(record.createdAt) }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_updatedAt') }}</span><span class="card-value">{{ formatDateTime(record.updatedAt) }}</span></div>
        </div>
      </template>

      <!-- kr 탭: gtin_products_kr -->
      <template v-else-if="activeTab === 'kr'">
        <div v-for="record in products" :key="record._id" class="mobile-card">
          <div class="card-header">
            <span class="card-gtin mono">{{ record.gtinCode || '-' }}</span>
            <a-tag v-if="record.status" :color="record.status === 'active' ? 'green' : 'default'">{{ record.status }}</a-tag>
          </div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_brandName') }}</span><span class="card-value">{{ record.brandName || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_modelName') }}</span><span class="card-value">{{ record.modelName || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_productLine') }}</span><span class="card-value">{{ record.productLine || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_names') }}</span><span class="card-value">{{ record.names ? (record.names.ko || record.names.zh || record.names.en) : '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_color') }}</span><span class="card-value">{{ record.color || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_diameter') }}</span><span class="card-value">{{ formatNumber(record.diameter) }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_thickness') }}</span><span class="card-value">{{ record.thickness || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_importSource') }}</span><span class="card-value">{{ record.importSource || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_importedAt') }}</span><span class="card-value">{{ formatDateTime(record.importedAt) }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_createdAt') }}</span><span class="card-value">{{ formatDateTime(record.createdAt) }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_updatedAt') }}</span><span class="card-value">{{ formatDateTime(record.updatedAt) }}</span></div>
        </div>
      </template>

      <!-- cn 탭: gtin_products_cn -->
      <template v-else>
        <div v-for="record in products" :key="record._id" class="mobile-card">
          <div class="card-header">
            <span class="card-gtin mono">{{ record.gtinCode || '-' }}</span>
            <a-tag v-if="record.status" :color="record.status === 'active' ? 'green' : 'default'">{{ record.status }}</a-tag>
          </div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_productCode') }}</span><span class="card-value mono">{{ record.productCode || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_isMapped') }}</span><span class="card-value"><a-tag :color="record.isMapped ? 'green' : 'default'">{{ record.isMapped ? '✓' : '-' }}</a-tag></span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_importSource') }}</span><span class="card-value">{{ record.importSource || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_importedAt') }}</span><span class="card-value">{{ formatDateTime(record.importedAt) }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_deleteYn') }}</span><span class="card-value"><a-tag :color="record.deleteYn === 'Y' ? 'red' : 'default'">{{ record.deleteYn || '-' }}</a-tag></span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_createdAt') }}</span><span class="card-value">{{ formatDateTime(record.createdAt) }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_products_col_updatedAt') }}</span><span class="card-value">{{ formatDateTime(record.updatedAt) }}</span></div>
        </div>
      </template>
    </div>

    <!-- 페이지네이션 (분리, 우측 정렬, show-total + size-changer) -->
    <div class="pagination-row">
      <a-pagination
        v-model:current="pagination.current"
        :total="pagination.total"
        :page-size="pageSize"
        :show-total="totalLabel"
        show-size-changer
        :page-size-options="['10', '20', '50', '100']"
        @change="onPageChange"
        @show-size-change="onPageSizeChange"
      />
    </div>

    <shortcut-help v-model:visible="showShortcutHelp" />
  </div>
</template>

<script>
// @MX:SPEC: SPEC-ECOUNT-FE-POLISH-001 Phase B-3

import { useKstTime } from './composables/useKstTime.js';
import { getProducts, getKrProducts, getCnProducts, extractApiError, parsePaginatedResponse } from '../../apis/ecountEtl.js';
import FieldOwnershipBadge from './_components/FieldOwnershipBadge.vue';
import RunStatusBadge from './_components/RunStatusBadge.vue';
import TimezoneToggle from './_components/TimezoneToggle.vue';
import ShortcutHelp from './_components/ShortcutHelp.vue';
import dealerEcountListMixin from '@/mixins/dealerEcountListMixin';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';

export default {
  name: 'EcountEtlProductsList',
  components: { FieldOwnershipBadge, RunStatusBadge, TimezoneToggle, ShortcutHelp, QuestionCircleOutlined },
  mixins: [dealerEcountListMixin],

  setup() {
    const { currentTz, toggleTz } = useKstTime();
    return { currentTz, toggleTz };
  },

  data() {
    return {
      loading: false,
      error: null,
      showShortcutHelp: false,
      activeTab: 'all',
      products: [],
      pageSize: 20,
      pagination: { current: 1, total: 0 },
      filters: { keyword: '' },
    };
  },

  computed: {
    dateColTitleFn() {
      return (labelKey) => () => (
        <span style="display: inline-flex; align-items: center; gap: 4px;">
          {this.$t(labelKey)}
          <a-tooltip title={this.$t('dealerGsp_tz_tooltip', { tz: this.timezoneInfo.label })}>
            <QuestionCircleOutlined style="color: rgba(0, 0, 0, 0.45); cursor: help" />
          </a-tooltip>
        </span>
      );
    },
    tableScrollX() {
      if (this.activeTab === 'all') return 1600;
      if (this.activeTab === 'kr') return 1700;
      return 1100; // cn
    },
    // 탭별 컬렉션 매핑 (ecount_product_master / gtin_products_kr / gtin_products_cn)
    columns() {
      const t = this.dateColTitleFn;
      if (this.activeTab === 'all') {
        // ecount_product_master
        return [
          { key: 'gtinCode', dataIndex: 'gtinCode', title: this.$t('ecountEtl_products_col_gtinCode'), width: 150 },
          { key: 'productName', dataIndex: 'productName', title: this.$t('ecountEtl_products_col_productName'), ellipsis: true, width: 260 },
          { key: 'ecountProductCode', dataIndex: 'ecountProductCode', title: this.$t('ecountEtl_products_col_ecountProductCode'), width: 180 },
          { key: 'gtin14', dataIndex: 'gtin14', title: this.$t('ecountEtl_products_col_gtin14'), width: 150 },
          { key: 'classCode', dataIndex: 'classCode', title: this.$t('ecountEtl_products_col_classCode'), width: 90 },
          { key: 'classCode2', dataIndex: 'classCode2', title: this.$t('ecountEtl_products_col_classCode2'), width: 90 },
          { key: 'classCode3', dataIndex: 'classCode3', title: this.$t('ecountEtl_products_col_classCode3'), width: 90 },
          { key: 'prodType', dataIndex: 'prodType', title: this.$t('ecountEtl_products_col_prodType'), width: 80 },
          { key: 'unit', dataIndex: 'unit', title: this.$t('ecountEtl_products_col_unit'), width: 70 },
          { key: 'regulatoryCode', dataIndex: 'regulatoryCode', title: this.$t('ecountEtl_products_col_regulatoryCode'), width: 160 },
          { key: 'gtinChecksumValid', dataIndex: 'gtinChecksumValid', title: this.$t('ecountEtl_products_col_gtinChecksumValid'), width: 100, align: 'center' },
          { key: 'deleteYn', dataIndex: 'deleteYn', title: this.$t('ecountEtl_products_col_deleteYn'), width: 80, align: 'center' },
          { key: 'status', dataIndex: 'status', title: this.$t('ecountEtl_products_col_status'), width: 90 },
          { key: 'createdAt', dataIndex: 'createdAt', title: t('ecountEtl_products_col_createdAt'), width: 170 },
          { key: 'updatedAt', dataIndex: 'updatedAt', title: t('ecountEtl_products_col_updatedAt'), width: 170 },
        ];
      }
      if (this.activeTab === 'kr') {
        // gtin_products_kr
        return [
          { key: 'gtinCode', dataIndex: 'gtinCode', title: this.$t('ecountEtl_products_col_gtinCode'), width: 150 },
          { key: 'brandName', dataIndex: 'brandName', title: this.$t('ecountEtl_products_col_brandName'), width: 130 },
          { key: 'modelName', dataIndex: 'modelName', title: this.$t('ecountEtl_products_col_modelName'), width: 160 },
          { key: 'productLine', dataIndex: 'productLine', title: this.$t('ecountEtl_products_col_productLine'), width: 160 },
          { key: 'names', dataIndex: 'names', title: this.$t('ecountEtl_products_col_names'), ellipsis: true, width: 200 },
          { key: 'color', dataIndex: 'color', title: this.$t('ecountEtl_products_col_color'), width: 80 },
          { key: 'diameter', dataIndex: 'diameter', title: this.$t('ecountEtl_products_col_diameter'), width: 100, align: 'right' },
          { key: 'thickness', dataIndex: 'thickness', title: this.$t('ecountEtl_products_col_thickness'), width: 90, align: 'right' },
          { key: 'deleteYn', dataIndex: 'deleteYn', title: this.$t('ecountEtl_products_col_deleteYn'), width: 80, align: 'center' },
          { key: 'status', dataIndex: 'status', title: this.$t('ecountEtl_products_col_status'), width: 90 },
          { key: 'importSource', dataIndex: 'importSource', title: this.$t('ecountEtl_products_col_importSource'), ellipsis: true, width: 200 },
          { key: 'originalRowNo', dataIndex: 'originalRowNo', title: this.$t('ecountEtl_products_col_originalRowNo'), width: 90, align: 'right' },
          { key: 'importedAt', dataIndex: 'importedAt', title: t('ecountEtl_products_col_importedAt'), width: 170 },
          { key: 'createdAt', dataIndex: 'createdAt', title: t('ecountEtl_products_col_createdAt'), width: 170 },
          { key: 'updatedAt', dataIndex: 'updatedAt', title: t('ecountEtl_products_col_updatedAt'), width: 170 },
        ];
      }
      // cn: gtin_products_cn
      return [
        { key: 'gtinCode', dataIndex: 'gtinCode', title: this.$t('ecountEtl_products_col_gtinCode'), width: 150 },
        { key: 'productCode', dataIndex: 'productCode', title: this.$t('ecountEtl_products_col_productCode'), width: 180 },
        { key: 'isMapped', dataIndex: 'isMapped', title: this.$t('ecountEtl_products_col_isMapped'), width: 90, align: 'center' },
        { key: 'deleteYn', dataIndex: 'deleteYn', title: this.$t('ecountEtl_products_col_deleteYn'), width: 80, align: 'center' },
        { key: 'status', dataIndex: 'status', title: this.$t('ecountEtl_products_col_status'), width: 90 },
        { key: 'importSource', dataIndex: 'importSource', title: this.$t('ecountEtl_products_col_importSource'), ellipsis: true, width: 220 },
        { key: 'importedAt', dataIndex: 'importedAt', title: t('ecountEtl_products_col_importedAt'), width: 170 },
        { key: 'createdAt', dataIndex: 'createdAt', title: t('ecountEtl_products_col_createdAt'), width: 170 },
        { key: 'updatedAt', dataIndex: 'updatedAt', title: t('ecountEtl_products_col_updatedAt'), width: 170 },
      ];
    },
  },

  mounted() {
    this.applyQueryToFilters();
    // SPEC-CHINA-ECOUNT-DEALER-FILTER-URL-SYNC-001: 필터 ↔ URL query 양방향 동기화 등록
    this.setupQueryFilterSync();
    this.fetchData();
  },

  methods: {
    onTabChange() { this.pagination.current = 1; this.fetchData(); },
    onSearch() {
      this.pagination.current = 1;
      this.fetchData();
    },
    onReset() {
      this.filters = { keyword: '' };
      this.pagination.current = 1;
      this.fetchData();
    },

    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const params = {
          page: this.pagination.current,
          limit: this.pageSize,
          keyword: this.filters.keyword || undefined,
        };
        let res;
        if (this.activeTab === 'kr') res = await getKrProducts(params);
        else if (this.activeTab === 'cn') res = await getCnProducts(params);
        else res = await getProducts(params);
        const parsed = parsePaginatedResponse(res);
        this.products = parsed.items || parsed.data || [];
        this.pagination.total = parsed.total ?? 0;
      } catch (e) {
        const err = extractApiError(e);
        const localized = this.$t(err.messageKey, err.messageParams);
        this.error = localized.startsWith('ecountEtl_') ? err.message : localized;
        this.products = [];
        this.pagination.total = 0;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.page-header { display: flex; align-items: center; margin-bottom: 16px; }
.page-title { font-size: 20px; font-weight: 600; }
.filter-actions { margin-bottom: 0; margin-left: auto; margin-right: 0; }

/* 딜러 패턴 답습 — 필터 폼 줄바꿈 정렬 */
.dealer-filter-form :deep(.ant-form-item) { margin-bottom: 12px; }
.dealer-filter-form :deep(.ant-form-item:last-child) { margin-bottom: 0; }
.filter-actions :deep(.ant-space) { flex-wrap: nowrap; }

.tab-with-tip { display: inline-flex; align-items: center; gap: 6px; }
.tab-tip-icon { color: rgba(0, 0, 0, 0.45); cursor: help; font-size: 13px; }

.toolbar-row {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px; padding: 0 4px;
}
.total-count { font-size: 13px; color: #666; }
.toolbar-controls { display: flex; gap: 8px; }

.pagination-row { display: flex; justify-content: flex-end; margin-top: 16px; }

/* 모바일 카드뷰 */
.mobile-card-list { display: flex; flex-direction: column; gap: 12px; }
.empty-state { padding: 32px 0; text-align: center; }
.mobile-card {
  background: #fff; border: 1px solid #f0f0f0; border-radius: 8px;
  padding: 12px; box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}
.card-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #f5f5f5;
}
.card-gtin { font-family: monospace; font-weight: 600; color: #333; font-size: 13px; }
.status-empty { color: #999; font-size: 12px; }
.card-field {
  display: flex; justify-content: space-between; align-items: baseline;
  font-size: 12px; padding: 4px 0;
}
.card-product-name { flex-direction: column; align-items: flex-start; gap: 4px; }
.card-product-name .card-value { color: #333; font-weight: 500; font-size: 14px; word-break: break-word; }
.card-label { color: #888; flex-shrink: 0; margin-right: 8px; }
.card-value { color: #333; text-align: right; word-break: break-all; }

@media (max-width: 767px) {
  .page-header { flex-wrap: wrap; gap: 8px; row-gap: 8px; }
  .toolbar-row { flex-direction: column; align-items: stretch; gap: 8px; }
  .toolbar-controls { justify-content: flex-end; }
  .pagination-row { justify-content: center; }
}
</style>
